import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { EntityProps, EntityState, setAllEntities, updateEntity, upsertEntity } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';

import { SelectedEntityState } from '../../shared/store/selected-entity.feature';
import { ActiviteCreateRequest } from '../activité-form/requests/create-request';
import { ActiviteDuplicateRequest } from '../activité-form/requests/duplicate-request';
import { ActiviteUpdateRequest } from '../activité-form/requests/update-request';
import { ActivitéService } from '../activité.service';
import { Activité } from '../models/activité.model';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

export function avecMéthodesActivités<_>() {
  return signalStoreFeature(
    {
      state: type<EntityState<Activité> & SelectedEntityState>(),
      props: type<EntityProps<Activité>>(),
    },
    withMethods((store) => ({
      chargerActivités: (state: { activités: Activité[] }) => patchState(store, setAllEntities(state.activités)),
      selectionnerActivité: (state: { id: string }) => patchState(store, { selectedEntityId: state.id }),
      ajouterActivité: (state: { activité: Activité }) => patchState(store, upsertEntity(state.activité)),
      modifierActivité: (state: { id: string; activite: Partial<Activité> }) =>
        patchState(store, updateEntity({ id: state.id, changes: state.activite })),
    })),
    withMethods((store, service = inject(ActivitéService)) => ({
      charger: rxMethod<{ idReferentiel?: string; nom: string }>(
        pipe(
          switchMap(({ idReferentiel, nom }) =>
            service.trouverTous(nom, idReferentiel).pipe(
              tapResponse({
                next: (result) => store.chargerActivités({ activités: result.items }),
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors du chargement des activités, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      ajouter: rxMethod<{
        nouvelleActivite: ActiviteCreateRequest;
        next: (activité: Activité) => void;
      }>(
        pipe(
          switchMap(({ nouvelleActivite: newActivite, next }) =>
            service.ajouter(newActivite).pipe(
              tapResponse({
                next: (activité) => {
                  store.ajouterActivité({ activité });
                  store.selectionnerActivité({ id: activité.id });
                  next(activité);
                },
                error: (err) => {
                  const toastrId = uuidv7();

                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: 'Une erreur est survenue lors de la création de cette activité, merci de réessayer.',
                      id: toastrId,
                    },
                    id: toastrId,
                  }),
                    console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{
        id: string;
        data: ActiviteUpdateRequest;
        next: () => void;
      }>(
        pipe(
          switchMap(({ id, data, next }) =>
            service.modifier(id, data).pipe(
              tapResponse({
                next: () => {
                  store.modifierActivité({
                    id,
                    activite: data,
                  });
                  next();
                },
                error: (err) => {
                  console.error("Error lors de la modification d'une activité : ", err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: 'Une erreur est survenue lors de la modification de cette activité, merci de réessayer.',
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      dupliquer: rxMethod<{
        data: ActiviteDuplicateRequest;
        next: (activité: Activité) => void;
      }>(
        pipe(
          switchMap(({ data, next }) =>
            service.dupliquer(data).pipe(
              tapResponse({
                next: (activité) => {
                  store.ajouterActivité({ activité });
                  store.selectionnerActivité({ id: activité.id });
                  next(activité);
                },
                error: (err) => {
                  console.error("Error lors de l'ajout d'une activité via le Store: ", err);
                  const toastrId = uuidv7();

                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: 'Une erreur est survenue lors de la création de cette activité, merci de réessayer.',
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
    })),
  );
}
